"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.FeatureFlag = void 0;
// The single registry of LaunchDarkly flag keys. A key here must match the
// flag key in the LaunchDarkly `foundry` project exactly (kebab-case
// `<area>-<description>`). See .product/feature-flags/usage-guide.md.
//
// `EXAMPLE` is a template placeholder the framework's own tests reference; it
// stays so those tests never depend on a product flag. Real flags live
// alongside it.
exports.FeatureFlag = {
    EXAMPLE: 'example-flag',
    // THE Invoicing V2 switch: one flag gates every V2 surface, because the
    // rebuild is too large for a piecewise rollout. There is no per-capability
    // flag and no per-order cutover. Service-context evaluations only, because a
    // per-user rollout would be a per-user partial migration. The code default
    // must track the deployed marker state at every stage (false before the
    // switch deploy, bumped to true in it) so an unreachable LaunchDarkly can
    // never move the switch in either direction. The durable half is the
    // `invoicing_v2_switch_marker` row, and the two halves must agree.
    INVOICING_V2: 'invoicing-v-2-enable',
    // The pro forma pool: the close rule reads unapplied pro forma cash and the panel shows it; off = pre-pool.
    INVOICING_V2_PRO_FORMA_POOL: 'invoicing-v-2-pro-forma-pool',
    // Gates the v0 BL-order invoicing experience: the Payments & Invoicing tab,
    // the trade-contract mirror, the document-chain ledger, and the
    // create-next / review endpoints. Off keeps the pre-tab layout and card
    // listing operators use today.
    INVOICING_V0: 'invoicing-v-0-enable',
    // THE canonical-value read switch: every deal-value consumer branches on this
    // one flag (per-consumer flags would allow the partial migration the rollout
    // forbids). Service-context evaluations only — a per-user rollout of a value
    // definition would be a per-user partial migration. The code default must
    // track the deployed SQL-consumer state at every stage (false before the
    // cut-over deploy, bumped to true in it) so an unreachable LaunchDarkly can
    // never strand app consumers on legacy while the SQL surfaces are canonical.
    INVOICING_CANONICAL_VALUE_READ: 'invoicing-canonical-value-read',
    // Shadow delta logging only — never changes any consumer output. Orthogonal
    // to the read switch (it can stay on after the flip as cheap monitoring).
    INVOICING_CANONICAL_VALUE_SHADOW: 'invoicing-canonical-value-shadow',
    // Gates the issue-time advance-application write: the fact row tying a BL
    // invoice's down-payment deduction to the order-level Advance it consumes,
    // plus the guards that keep it honest (received-basis, over-deduction,
    // advance-void, payment-deletion). Off = today's issue path exactly — no
    // resolution, no guards, no row. Must not flip on in prd before the legacy
    // carrier backfill is applied there: an unbackfilled EasyBill-era order looks
    // fully open to the received-basis guard and could be deducted twice.
    INVOICING_ADVANCE_APPLICATION_WRITE: 'invoicing-advance-application-write',
    // Gates the advance-tracking READS built on the fact row: the order-side
    // funding status (billed / received / consumed / open) and the per-BL applied
    // total, plus the form pre-fill that suggests the side's open advance instead
    // of the shipment-count-ratio guess. Off = byte-identical responses — the new
    // response fields are omitted entirely (never emitted-but-empty), and field
    // presence is the one gate every downstream reader keys on. Independent of the
    // write flag: the reads are correct with zero rows (pure legacy carrier).
    INVOICING_ADVANCE_TRACKING_READ: 'invoicing-advance-tracking-read',
    // Master switch for DATEV dispatch (invoicing tech-spec 01). Off /
    // LD-unreachable = dark: no sends, no renders from the dispatch path; the
    // obligation stays derivable and the reconciler keeps reporting.
    DATEV_DISPATCH_ENABLED: 'datev-dispatch-enabled',
    // Gates the M365 Graph send transport (invoicing tech-spec 01, card #1201).
    // Off / LD-unreachable = the DATEV dispatch stays dark (no external send);
    // must be on AND datev-dispatch-enabled on for a live send.
    DATEV_M365_TRANSPORT: 'datev-m365-transport-enabled',
    // Master switch for the GoBD DATEV archive (invoicing tech-spec 03). Off /
    // LD-unreachable = archive stays dark: no writes to the Object-Lock bucket.
    DATEV_ARCHIVE_ENABLED: 'datev-archive-enabled',
    // Master switch for ZUGFeRD generation at issuance (tech-spec 05). Off /
    // LD-unreachable = today's plain-PDF issuance, untouched.
    ZUGFERD_GENERATION_ENABLED: 'zugferd-generation-enabled',
    // LME reference resolution at matching (Fizzy #1089): Formula/Index ticket
    // sides with no trader-entered lme_price get the latest official LME
    // settlement (cash bid) frozen onto the ticket at match time, so floating
    // deals carry a provisional price (order buy/sell price + margin) from
    // order creation. Changes the approval evaluator's margin inputs — off /
    // LD-unreachable = today's behavior: no fill, floating prices stay null.
    LME_REFERENCE_AT_MATCHING: 'lme-reference-at-matching',
    // Rolling-aware FX Cover on the order-detail Hedge Status card. Off = the
    // legacy per-pair aggregation; on = the single latest-leg open position plus
    // an expandable roll history. Off is the safe default and must stay correct.
    FX_COVER_ROLLING: 'fx-cover-rolling',
    // Conditionally-mandatory Freight Forwarder on BL orders (Fizzy #1090):
    // required when the parent order's buy ticket incoterm means the buyer
    // arranges freight (NOT in SELLER_ARRANGES_FREIGHT_INCOTERMS). Gates the
    // 422 on the PATCH /bl-level/:id save and the required-field UI on the
    // Transportation Summary. Off / LD-unreachable = today's behavior: the
    // field stays optional everywhere. The creatable combobox itself is NOT
    // gated — it is strictly more permissive than the old closed list.
    FREIGHT_FORWARDER_REQUIRED: 'freight-forwarder-required',
    // Gates the BL core-values record: the Initial stamp written at BL creation
    // and the derived Initial/Provisional/Final reads built on the append-only
    // value log. Off = no rows written and no reads served — BL creation and
    // every existing pricing surface behave exactly as today. Flip on to start
    // populating the log before any display consumer ships.
    INVOICING_CORE_VALUES: 'invoicing-core-values',
    // Gates the sell-side Final settlement's composite PDF: the full chain history
    // (down payment, provisional, credit/debit notes, final) rendered as one
    // document beneath the Final's own body, instead of the single-event body
    // alone. Off = the Final renders only its own values, exactly as today. Buy-
    // side rows and every other kind are unaffected.
    INVOICING_CHAIN_COMPOSITE_PDF: 'invoicing-chain-composite-pdf',
    // Gates the order-level downpayment allocation editor: pin-and-derive share
    // editing, saves that leave part of the pool unallocated (Σ shares ≤ pool),
    // and the chain lock that freezes a BL's share once any document hangs under
    // its advance in the chain. Off / LD-unreachable = today's coverage picker
    // and strict sum-to-total saves, untouched.
    INVOICING_ORDER_DP_ALLOCATION: 'invoicing-order-dp-allocation',
    // Gates the whole issuer-bank feature on BOTH sides: the review-dialog bank
    // picker (frontend) and the issue-time bank requirement (backend). Off /
    // LD-unreachable = no bank field renders and issuance no longer requires a
    // selected bank, so a sell invoice issues without one (pre-catalog behavior;
    // the PDF payment block falls back to the constant account). Flip on only
    // after the entity bank catalog is populated and a bank-management UI exists
    // for operators to resolve missing-bank states — otherwise the picker strands
    // invoices in Draft with no way to add a bank.
    INVOICING_ENTITY_BANK: 'invoicing-entity-bank',
    // Stop-the-bleed for zero-price hedge executions (Fizzy #1209 step 4): when
    // on, the booking endpoints (record execution, roll pair, FX close) answer
    // 422 to an executed price/rate ≤ 0, and position matching requires a typed
    // rate for FX instead of booking the 0 placeholder. Off / LD-unreachable =
    // today's behavior: FX matches may book at 0 and rely on the StoneX rate
    // backfill (stonex-fx-trade-eod-sync) to fill the real rate later.
    HEDGING_REQUIRE_EXECUTION_PRICE: 'hedging-require-execution-price',
    // FX hedge rolling. Rolls exist for metals only; the desk works around that
    // by booking each FX roll as a fresh INITIAL_HEDGE, which lands in the Data
    // Tape's locked FX rate. On = the roll request and the "Request roll" action
    // accept FX, so the pair is typed ROLL_CLOSE/ROLL_OPEN and drops out of the
    // locked-rate average by type. Off / LD-unreachable = today's behavior: the
    // roll request rejects FX and the action stays hidden on FX rows.
    HEDGING_FX_ROLL: 'hedging-fx-roll',
    // Gates the FX-trades step of the scheduled StoneX EOD sync (Fizzy #1213):
    // syncFxTrades over a rolling window + the idempotent rate/prompt backfill
    // onto zero-price FX executions. Off / LD-unreachable = today's cron exactly
    // (positions + cash only). Must NOT flip on in prd before Fizzy #1209's roll
    // retype lands: backfilling the mistyped INITIAL_HEDGE roll legs would pull
    // roll rates into the Data Tape's locked-rate average (fence contract §4.4).
    STONEX_FX_TRADE_EOD_SYNC: 'stonex-fx-trade-eod-sync',
    // Bank-feed seam (WP-4..WP-8): all five registered together so later WPs
    // never touch this registry concurrently. Each consumer swap branches on its
    // own flag; off = the legacy path byte-for-byte. Evaluation sites arrive
    // with their WPs — every evaluator must use a false/unreachable default.
    BANK_FEED_TRANSACTIONS_READ: 'bank-feed-transactions-read',
    BANK_FEED_CASH_BALANCES_READ: 'bank-feed-cash-balances-read',
    BANK_FEED_LEDGER_READ: 'bank-feed-ledger-read',
    BANK_FEED_CASH_EVENT_INGEST: 'bank-feed-cash-event-ingest',
    // Effective multi-source state is the runtime conjunction with the ledger
    // read + cash-event ingest flags (WP-8) — never evaluated alone.
    BANK_FEED_MULTI_SOURCE_MATCHING: 'bank-feed-multi-source-matching',
    ELIGIBILITY_FENCE_API_CHECK: 'eligibility-fence-api-check',
    // Master switch for the hourly Rivonia auto-assign job (prd-rivonia-auto-
    // assign.md §5.1). Off / LD-unreachable = a dark tick with zero external
    // footprint: no DB query, no enqueue, no Sentry check-in. Permanent, like
    // DATEV_DISPATCH_ENABLED — not a rollout flag with a removal date.
    ELIGIBILITY_AUTO_ASSIGN_RIVONIA: 'eligibility-auto-assign-rivonia',
    // Net-terms payment triggers (Invoice / Fixation / DP) classify post_release
    // per the 7 August 2026 ruling (Fizzy #1492). On: a net-terms sell line
    // hard-fails the payment rule. Off / LD-unreachable = the pre-ruling
    // classification exactly: net terms stay provisional and the deal surfaces
    // unresolved (yellow), so an unreachable LaunchDarkly can never hard-block a
    // deal the old code let through. Evaluated once per engine batch in service
    // context — a verdict must never vary per user. Deploy precondition: create
    // the flag in the LD dashboard. Cleanup: remove once the four affected BLs
    // read correctly in prd and the hard-block has held for a full cycle.
    ELIGIBILITY_NET_TERMS_HARD_BLOCK: 'eligibility-net-terms-hard-block',
    // Eligibility-gated Data Tape selection (Fizzy #1247/#1256). On: the tape
    // view derives each row's fence status (pending / submitted / not_applicable)
    // from its Rivonia assignment + submission history, and the outbound Fence
    // payload is restricted to Rivonia-assigned, not-yet-submitted BLs. Off /
    // LD-unreachable = today's inclusive tape exactly (absent state row =
    // pending; payload = everything not already submitted).
    // Rollout: the eligibility-auto-assign job that writes the Rivonia
    // assignment ships in #1199 (this branch) — until it lands
    // `financing_partner` is set by manual assignment only, so verify Rivonia
    // assignments are current before enabling in any environment or the payload
    // will be near-empty.
    DATA_TAPE_ELIGIBILITY_GATED_SELECTION: 'data-tape-eligibility-gated-selection',
    // Submission-time eligibility re-check (Fizzy #1279). On: /prepare and
    // /submit re-evaluate the selected BLs against the engine and hold back any
    // line that is not `eligible` (a curable-only pass, not_eligible, or
    // not_assessed are all dropped) before the Fence payload is built — the
    // auto-assign job only ever assigns, never unassigns, so a BL that flipped
    // ineligible after assignment would otherwise ride the payload. The record
    // stores only what was sent; dropped BLs surface in the response, not the
    // row. Off / LD-unreachable = no re-check, payload built from the full
    // selection (today's behavior). Targeted by lender context: enable in LD for
    // Rivonia only — other lenders have no ruleset, so every BL returns
    // not_assessed and the whole submission would 422.
    DATA_TAPE_SUBMISSION_ELIGIBILITY_RECHECK: 'data-tape-submission-eligibility-recheck',
    // Gates the concentration-cap surfaces (RG-E / #919): the standing caps read
    // API + view (#1307/#1308) and, later, the B3 simulation panel (#1309). On:
    // the `data-tape/v2/caps` reads answer with the per-cap result set and the
    // caps view renders. Off / LD-unreachable = the endpoints 404 and the UI
    // hides — a production deploy is inert until the flag flips (view-only, no
    // write path, so enabling carries no data risk). The API and the UI key on
    // the SAME flag so they enable together.
    DATA_TAPE_CONCENTRATION_CAPS: 'data-tape-concentration-caps',
    // Gates the claims→books bridge: the credit note + paid payment auto-booked
    // when a claim is saved into settlement option "Credit Note" + status
    // "Paid". Off / LD-unreachable = no auto-booking — ops books credit notes in
    // EasyBill and they reach the deal through the EasyBill sync, so the bridge
    // note is always a duplicate in that workflow. Off is the deliberate steady
    // state until a claim↔credit-note link feature exists; the transition-only
    // firing rule still applies when the flag is on.
    CLAIMS_CREDIT_NOTE_BRIDGE: 'claims-credit-note-bridge-enable',
    // WHO the transport parties are: every registry surface keys on this one flag
    // — the three transport values in the service-type vocabulary
    // /reference/enums serves, the alias display + editor on the company page,
    // the registry-backed ticket transportation-company and freight-forwarder
    // pickers, the lien register surfaces, and the B/L party links. Off /
    // LD-unreachable = today's behaviour on every surface. The registry tables
    // and the lookup swap are NOT gated — they are behaviour-identical while only
    // backfilled 'freight' pairings exist. Deploy precondition: create the flag in
    // the LD dashboard with client-side SDK availability ticked.
    //
    // HOW a B/L order reads and is written is a separate rollout on
    // BL_ORDER_REVAMP_V0 below. Neither flag gates the other's surfaces.
    TRANSPORT_REGISTRY_V0: 'transport-registry-v0',
    // The ONE flag for the Lender Hedge Allocation initiative (WP-1: the roll
    // ?account branch, the accountScope/accountNumber/ambiguousRefs fields, the
    // prompt-date guard). Off / LD-unreachable = pre-LHA responses byte-for-byte.
    LENDER_HEDGING_ALLOCATION: 'lender-hedging-allocation-enabled',
    // The inline transport-party create asks for the full company profile: the
    // dialog behind every registry picker swaps its name-only form for the full
    // required set (trader owner, countries, VAT id), and the extraction-review
    // carrier picker gains a create action it never had. Off / LD-unreachable =
    // today's name-only dialog on the existing surfaces and no create action on
    // the review picker. The endpoint accepts both payload shapes regardless of
    // this flag, so a mid-session flip never strands an open form. Rides inside
    // TRANSPORT_REGISTRY_V0 surfaces — with the registry off this flag has no
    // surface to act on. Deploy precondition: create the flag in the LD
    // dashboard with client-side SDK availability ticked. Cleanup: remove once
    // the full form is stable in prod and ops confirms carriers arrive complete.
    CARRIER_FULL_PROFILE_CREATE: 'carrier-full-profile-create',
    // The ONE flag for the additional-cost transfer reference (both PRDs in
    // `.product/additional-costs/`): the field on the Other Costs Add/Edit form
    // and its payable-only requirement, plus the read of it on the cost card.
    // Keyed on the field rather than the requirement because it gates the
    // display too. Off / LD-unreachable = today's form and card exactly — no
    // field, no validation, and every existing create/edit/delete unchanged.
    // The column is NOT gated: it is additive and nullable, so it is inert
    // while the flag is off. Enforcement is create-only, so flipping the flag
    // on never makes a pre-existing cost unsaveable. Deploy precondition:
    // create the flag in the LD dashboard with client-side SDK availability
    // ticked. Cleanup: remove once every new payable cost carries a reference
    // and ops confirms the rule holds.
    ADDITIONAL_COSTS_TRANSFER_REFERENCE: 'additional-costs-transfer-reference',
    // The B/L order revamp: one B/L card in place of the summary and the
    // extraction, the edit path behind it, and the mirror that keeps `bl_order`
    // and `bl_extraction` holding the same values. Deliberately NOT
    // `transport-registry-v0` — that flag gates who the transport parties are,
    // this one gates how a B/L order reads and is written. The two roll out
    // separately.
    //
    // Off / LD-unreachable = today's behaviour on every surface, including the
    // narrow propagation the mirror replaces: the operator PATCH mirrors
    // `shipping_line` alone, the extraction paths fill `bl_number` and the
    // loaded quantity only, and no field provenance is stamped. Deploy
    // precondition: create the flag in the LD dashboard with client-side SDK
    // availability ticked.
    BL_ORDER_REVAMP_V0: 'bl-order-revamp-v0',
    // Compulsory payment-line trigger (Fizzy #1581): when a ticket schedule line
    // carries a percentage > 0, its trigger is required — downpayment_trigger for
    // the advance line, trigger_event for BL tranches. A 0% line is exempt (both
    // kinds). Enforced on the ticket create/edit form (frontend) and guarded on
    // the POST/PATCH /tickets write paths (backend). Off / LD-unreachable =
    // today's behaviour: BL tranche trigger_event required unconditionally,
    // downpayment trigger unchecked — so an unreachable LaunchDarkly never makes
    // a formerly-saveable ticket unsaveable. Deploy precondition: create the flag
    // in the LD dashboard with client-side SDK availability ticked (the frontend
    // reads it). Cleanup: remove once stable in prd and the one-time retroactive
    // blank-trigger sweep has run.
    TICKET_PAYMENT_TRIGGER_REQUIRED: 'ticket-payment-trigger-required',
    // Borrowing-base surfaces on the Active Deals eligibility page (Fizzy #1580):
    // the per-BL "Borrowing Base" + "Advance (90%)" table columns, the same two
    // columns + a currency column + a "TOTAL (eligible)" footer in the Download
    // XLS, and the "Eligible Borrowing Base" summary card. Off / LD-unreachable =
    // the page exactly as before the feature — no borrowing-base column, card, or
    // export columns anywhere (the additive `buy_price` / `buy_currency` wire
    // fields stay inert). Read once in the Active Deals tab and threaded down as a
    // prop, so a false/unreachable default hides every surface together. Deploy
    // precondition: create the flag in the LD dashboard with client-side SDK
    // availability ticked. Cleanup: remove once Rivonia is live on the export and
    // the columns are stable in prd.
    FINANCING_BORROWING_BASE: 'financing-borrowing-base',
    // Metals contract-reference picker on the Allocate Broker Fill form (Fizzy
    // #1681): the metal-trade-candidates read endpoint and the picker that
    // replaces the free-text reference input for metal fills, mirroring the FX
    // picker. Off / LD-unreachable = today's behaviour exactly — the endpoint
    // 404s and the form keeps the free-text field. The ingest columns backing it
    // (asset_class, lots, contract_size, security_description on the synced
    // trades table) are NOT gated: they are additive and nullable, stamped by
    // the existing stonex-fx-trade-eod-sync step. Deploy precondition: create
    // the flag in the LD dashboard with client-side SDK availability ticked
    // (the frontend reads it). Cleanup: remove once the desk books metal fills
    // through the picker in prd and typed references stop appearing.
    METAL_TRADE_REF_PICKER: 'metal-trade-ref-picker',
};
//# sourceMappingURL=feature-flags.js.map